import { useEffect, useId, useRef, useState } from 'react'
import { api, ApiError, type DesignFilm, type LayoutAsset } from '../lib/api'
import { DESIGN_MEDIA_ACCEPT, megabytes } from '../lib/signageEdit'
import { explain } from '../lib/staffErrors'
import { roomFor, uploadRefusal } from '../lib/signageRefusals'
import type { StaffKey } from '../lib/staffText'
import { Button, Input, Label, Switch, TextField } from '@heroui/react'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { IconButton, Notice, StudioFilePicker, StudioSelect, Tick } from './heroParts'
import { MediaSource } from './MediaSource'
import {
  DAYS,
  MAX_ITEM_SECONDS,
  MIN_ITEM_SECONDS,
  endForInput,
  endForStorage,
  filmChoices,
  mediaKindOf,
  moveItem,
  newMediaItem,
  newSlot,
  withSound,
  type MediaTarget,
} from './media'
import type { LayoutElement, LayoutMedia, MediaItem, MediaKind, MediaSlot } from './types'

/**
 * The playlist's panel in the Designs editor: what plays, and what plays at set
 * times instead.
 *
 * In the reader's language, unlike the rest of the editor, because the index's
 * decision 6 asks new staff copy to start in all three (batch 4, part L). This
 * directory is not read by noEnglishLeft, so every word a person sees here has to
 * come through say(): nothing else would catch one that did not.
 *
 * Times are the branch's wall clock, kept as the "HH:MM" strings the time boxes
 * give and the server reads. Nothing here makes a Date of them: a Date would pass
 * them through the zone of the designer's browser, and a designer in another
 * city would move every advert on the wall by the difference.
 *
 * Mounted once per playlist (the inspector keys it by the element's id), so that
 * nothing of one playlist's panel — an upload on its way, a notice — carries over
 * to another's.
 */

const DAY_KEYS: Record<number, StaffKey> = {
  1: 'designs.media.day.1', 2: 'designs.media.day.2', 3: 'designs.media.day.3',
  4: 'designs.media.day.4', 5: 'designs.media.day.5', 6: 'designs.media.day.6',
  7: 'designs.media.day.7',
}

const KIND_KEYS: Record<MediaKind, StaffKey> = {
  image: 'designs.media.picture',
  video: 'designs.media.film',
}

/** A film's length in seconds from its header (asset records keep none); undefined if unread in 10 s. */
function filmLength(src: string): Promise<number | undefined> {
  return new Promise((resolve) => {
    const probe = document.createElement('video')
    probe.muted = true
    probe.preload = 'metadata'
    const done = (seconds: number | undefined) => {
      window.clearTimeout(timer)
      probe.onloadedmetadata = probe.onerror = null
      probe.removeAttribute('src')
      probe.load()
      resolve(seconds)
    }
    const timer = window.setTimeout(() => done(undefined), 10_000)
    probe.onloadedmetadata = () => done(probe.duration)
    probe.onerror = () => done(undefined)
    probe.src = src
  })
}

export function MediaInspector({
  element,
  onChange,
  onAddItem,
}: {
  element: LayoutElement
  onChange: (element: LayoutElement) => void
  /** Adds a finished upload to the document as it is when the upload finishes,
   *  found by the element's id (withMediaItem). Not onChange: an upload can
   *  outlive this panel, and onChange holds the document as it was when the
   *  upload began, so calling it then would put back everything edited since. */
  onAddItem: (elementId: string, target: MediaTarget, item: MediaItem) => void
}) {
  const { lang, say } = useStaffText()
  const ids = useId()
  const [assets, setAssets] = useState<LayoutAsset[]>([])
  const [uploading, setUploading] = useState(false)
  const [notice, setNotice] = useState('')
  // 'full': no film fits; 'short': a shorter one might.
  const [spaceFull, setSpaceFull] = useState<'' | 'full' | 'short'>('')
  const [wanted, setWanted] = useState(0)

  useEffect(() => {
    api
      .assets()
      .then((r) => setAssets(r.assets.filter((a) => mediaKindOf(a.contentType) !== null)))
      .catch(() => setAssets([]))
  }, [])

  const media: LayoutMedia = element.media ?? { items: [] }
  const setMedia = (next: LayoutMedia) => onChange({ ...element, media: next })
  const schedule = media.schedule ?? []
  const setSlot = (i: number, slot: MediaSlot) =>
    setMedia({ ...media, schedule: schedule.map((s, j) => (j === i ? slot : s)) })

  // The source first: a saved playlist is edited on the Signage page, so there is nothing more here.
  const source = <MediaSource media={media} onChange={setMedia} />
  if (media.source === 'playlist') return <div className="studio-media">{source}</div>

  // Asks the designer's own browser whether it can play a film. The server does
  // not look inside the file; this is the only check of the codec there is. Not
  // MediaPlayer, and not attached to the page: nothing is shown, so nothing needs
  // the stall watchdog, and only the header is fetched.
  const checkPlayable = (assetId: string) => {
    const probe = document.createElement('video')
    probe.muted = true
    probe.preload = 'metadata'
    const release = () => {
      probe.removeAttribute('src')
      probe.load()
    }
    probe.onloadedmetadata = release
    probe.onerror = () => {
      setNotice(say('designs.media.cannotPlay'))
      release()
    }
    probe.src = api.assetURL(assetId)
  }

  // A library item, added once a film's length is known (a picture at once).
  const addChosen = (asset: LayoutAsset, kind: MediaKind, target: MediaTarget) => {
    const elementId = element.id
    const addLater = onAddItem
    if (kind === 'image') addLater(elementId, target, newMediaItem(asset.id, kind))
    else void filmLength(api.assetURL(asset.id)).then((d) => addLater(elementId, target, newMediaItem(asset.id, kind, d)))
  }

  const upload = async (file: File, target: MediaTarget) => {
    // Taken now: this panel belongs to one element for its whole life, and the
    // callback from this render knows which design the upload began in.
    const elementId = element.id
    const addLater = onAddItem
    setUploading(true)
    setNotice('')
    setWanted(file.size)
    // Read from the file itself while it uploads: no second download.
    const local = file.type.startsWith('video/') ? URL.createObjectURL(file) : ''
    const length = local ? filmLength(local) : Promise.resolve(undefined)
    try {
      const asset = await api.uploadAsset(file)
      const kind = mediaKindOf(asset.contentType)
      if (kind) {
        setAssets((current) => [asset, ...current.filter((a) => a.id !== asset.id)])
        addLater(elementId, target, newMediaItem(asset.id, kind, kind === 'video' ? await length : undefined))
      }
      if (kind === 'video') checkPlayable(asset.id)
      setSpaceFull('')
    } catch (err) {
      setNotice(uploadRefusal(err, lang))
      if (err instanceof ApiError && err.code === 'VIDEO_SPACE_FULL') setSpaceFull(err.reason === 'full' ? 'full' : 'short')
    } finally {
      setUploading(false)
      if (local) void length.finally(() => URL.revokeObjectURL(local))
    }
  }

  const list = (items: MediaItem[], set: (items: MediaItem[]) => void, target: MediaTarget) => (
    <ItemList
      items={items}
      assets={assets}
      uploading={uploading}
      onChange={set}
      onAdd={(asset, kind) => addChosen(asset, kind, target)}
      onUpload={(file) => void upload(file, target)}
    />
  )

  return (
    <div className="studio-media">
      {source}
      {/* A group named by its heading and described by its hint, so that a
          screen reader arriving at the picker hears which list it adds to. The
          hint is for the ear only: the list shows its own order. */}
      <div role="group" className="studio-media" aria-labelledby={`${ids}-items`} aria-describedby={`${ids}-items-hint`}>
        <span className="studio-section" id={`${ids}-items`}>{say('designs.media.items')}</span>
        <span className="sr-only" id={`${ids}-items-hint`}>{say('designs.media.itemsHint')}</span>
        {list(media.items, (items) => setMedia({ ...media, items }), 'own')}
      </div>

      <span className="studio-section" title={say('designs.media.scheduleHint')}>
        {say('designs.media.schedule')}
      </span>
      {schedule.map((slot, i) => (
        <div
          key={`slot-${i}`}
          role="group"
          className="studio-slot"
          aria-labelledby={`${ids}-time-${i}`}
        >
          <div className="studio-between" style={{ alignItems: 'center' }}>
            <strong id={`${ids}-time-${i}`}>{say('designs.media.timeN', { n: i + 1 })}</strong>
            <IconButton
              tip={say('designs.media.removeTime')}
              aria-label={say('designs.media.removeTimeN', { n: i + 1 })}
              onPress={() => setMedia({ ...media, schedule: schedule.filter((_, j) => j !== i) })}
            >
              <Icon name="trash" size={16} />
            </IconButton>
          </div>
          <fieldset className="studio-field">
            <legend className="studio-field__label">{say('designs.media.days')}</legend>
            <div className="studio-checks">
              {DAYS.map((day) => (
                <Tick
                  key={day}
                  label={say(DAY_KEYS[day]!)}
                  checked={slot.days.includes(day)}
                  onChange={(on) =>
                    setSlot(i, {
                      ...slot,
                      days: on ? [...slot.days.filter((d) => d !== day), day].sort((a, b) => a - b) : slot.days.filter((d) => d !== day),
                    })
                  }
                />
              ))}
            </div>
          </fieldset>
          <div className="studio-row2">
            <TextField className="studio-field" value={slot.from} onChange={(v) => setSlot(i, { ...slot, from: v })}>
              <Label>{say('designs.media.from')}</Label>
              <Input variant="secondary" type="time" step={60} />
            </TextField>
            <TextField className="studio-field" value={endForInput(slot.to)} onChange={(v) => setSlot(i, { ...slot, to: endForStorage(v) })}>
              <Label title={say('designs.media.toHint')}>{say('designs.media.to')}</Label>
              <Input variant="secondary" type="time" step={60} />
            </TextField>
          </div>
          {list(slot.items, (items) => setSlot(i, { ...slot, items }), i)}
        </div>
      ))}
      <Button
        variant="secondary"
        size="sm"
        className="studio-panel__act"
        aria-describedby={`${ids}-schedule-hint`}
        onPress={() => setMedia({ ...media, schedule: [...schedule, newSlot()] })}
      >
        <Icon name="plus" size={14} /> {say('designs.media.addTime')}
      </Button>
      {notice && <Notice role="alert">{notice}</Notice>}
      {spaceFull && (
        <OldFilms
          onDeleted={(id) => setAssets((current) => current.filter((a) => a.id !== id))}
          wanted={wanted}
          onRoom={() => {
            setSpaceFull('')
            setNotice('')
          }}
          onNone={() => spaceFull === 'full' && setNotice(say('designs.media.spaceFullInUse'))}
        />
      )}
    </div>
  )
}

/** The design films nothing live plays, to delete when the film space is full. */
function OldFilms({
  onDeleted,
  onNone,
  onRoom,
  wanted,
}: {
  onDeleted: (id: string) => void
  onNone: () => void
  onRoom: () => void
  wanted: number
}) {
  const { lang, say } = useStaffText()
  const [films, setFilms] = useState<DesignFilm[]>([])
  const [waiting, setWaiting] = useState<DesignFilm[]>([])
  const [asking, setAsking] = useState('')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState('')

  // first: only the opening read may say every film is in use; after a delete, space was freed.
  const load = (first = false) =>
    api
      .designFilms()
      .then((r) => {
        const { deletable, waiting, allInUse } = filmChoices(r.films)
        setFilms(deletable)
        setWaiting(waiting)
        if (first && allInUse) onNone()
        if (!first && roomFor(r.freeBytes, wanted)) onRoom()
      })
      .catch((err) => setNotice(explain(err, lang, 'common.unreachable')))
  useEffect(() => {
    void load(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const remove = async (film: DesignFilm) => {
    setBusy(true)
    setNotice('')
    try {
      await api.deleteFilm(film.id, film.versions)
      onDeleted(film.id)
    } catch (err) {
      setNotice(explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
      setAsking('')
      await load()
    }
  }

  if (films.length === 0 && waiting.length === 0 && !notice) return null
  return (
    <div className="studio-media">
      <strong>{say('designs.media.oldFilms')}</strong>
      <ul className="studio-items">
        {films.map((f) => (
          <li key={f.id} className="studio-item">
            <span className="studio-item__name">
              {f.filename} · {megabytes(f.sizeBytes, lang)} {say('signage.size')}
            </span>
            <Button
              variant={asking === f.id ? 'danger' : 'ghost'}
              size="sm"
              isDisabled={busy}
              onPress={() => (asking === f.id ? void remove(f) : setAsking(f.id))}
            >
              {asking !== f.id
                ? say('common.delete')
                : f.versions > 0
                  ? say('designs.media.alsoVersions', { n: f.versions })
                  : say('signage.deleteAsk')}
            </Button>
          </li>
        ))}
        {waiting.map((f) => (
          <li key={f.id} className="studio-item">
            <span className="studio-item__name">
              {f.filename} · {megabytes(f.sizeBytes, lang)}
            </span>
            <span className="studio-muted">{say('designs.media.newNotYours')}</span>
          </li>
        ))}
      </ul>
      {notice && <Notice role="alert">{notice}</Notice>}
    </div>
  )
}

type Move = 'earlier' | 'later'

/** One list of things to play: its rows, a picker, and an upload. */
function ItemList({
  items,
  assets,
  uploading,
  onChange,
  onAdd,
  onUpload,
}: {
  items: MediaItem[]
  assets: LayoutAsset[]
  uploading: boolean
  onChange: (items: MediaItem[]) => void
  onAdd: (asset: LayoutAsset, kind: MediaKind) => void
  onUpload: (file: File) => void
}) {
  const { say } = useStaffText()
  const nameOf = (assetId: string) => assets.find((a) => a.id === assetId)?.filename ?? assetId
  const set = (i: number, item: MediaItem) => onChange(items.map((it, j) => (j === i ? item : it)))

  // A moved row is drawn afresh (its key names its place), so the button that was
  // pressed is gone and focus would fall to the page. It is put back on the same
  // button of the row that moved, or on its other one where that is now disabled
  // at the top or the bottom, so a keyboard can press Earlier three times running.
  const buttons = useRef(new Map<string, HTMLButtonElement>())
  const [refocus, setRefocus] = useState<{ index: number; which: Move } | null>(null)
  useEffect(() => {
    if (!refocus) return
    const same = buttons.current.get(`${refocus.index}:${refocus.which}`)
    const other = buttons.current.get(`${refocus.index}:${refocus.which === 'earlier' ? 'later' : 'earlier'}`)
    ;(same && !same.disabled ? same : other)?.focus()
    setRefocus(null)
  }, [refocus])
  const keep = (key: string) => (button: HTMLButtonElement | null) => {
    if (button) buttons.current.set(key, button)
    else buttons.current.delete(key)
  }
  const move = (from: number, which: Move) => {
    const to = which === 'earlier' ? from - 1 : from + 1
    onChange(moveItem(items, from, to))
    setRefocus({ index: to, which })
  }


  return (
    <div className="studio-media">
      {items.length === 0 && <small className="studio-hint">{say('designs.media.empty')}</small>}
      {items.length > 0 && (
      <ol className="studio-items">
      {items.map((item, i) => {
        const name = nameOf(item.assetId)
        return (
          <li key={`${i}:${item.assetId}`} className="studio-item" data-film={item.kind === 'video' || undefined}>
            {/* A picture is shown as itself; a film by a mark, because loading
                forty megabytes to draw a thumbnail is not a thumbnail. */}
            <span className="studio-item__thumb" title={say(KIND_KEYS[item.kind])}>
              {item.kind === 'image' ? <img src={api.assetURL(item.assetId)} alt="" loading="lazy" /> : <Icon name="play" size={16} />}
            </span>
            <span className="studio-item__name">
              {i + 1}. {name}
            </span>
            <span className="studio-item__tools">
              <IconButton
                ref={keep(`${i}:earlier`)}
                tip={say('designs.media.earlier')}
                aria-label={say('designs.media.earlierNamed', { name })}
                isDisabled={i === 0}
                onPress={() => move(i, 'earlier')}
              >
                <Icon name="up" size={15} />
              </IconButton>
              <IconButton
                ref={keep(`${i}:later`)}
                tip={say('designs.media.later')}
                aria-label={say('designs.media.laterNamed', { name })}
                isDisabled={i === items.length - 1}
                onPress={() => move(i, 'later')}
              >
                <Icon name="down" size={15} />
              </IconButton>
              <IconButton
                tip={say('designs.media.remove')}
                aria-label={say('designs.media.removeNamed', { name })}
                onPress={() => onChange(items.filter((_, j) => j !== i))}
              >
                <Icon name="close" size={15} />
              </IconButton>
            </span>
            <label className="studio-item__secs">
              <Input
                variant="secondary"
                type="number"
                min={MIN_ITEM_SECONDS}
                max={MAX_ITEM_SECONDS}
                step={1}
                aria-label={say('designs.media.secondsFor', { name })}
                title={say('designs.media.secondsHint')}
                value={item.seconds ?? ''}
                // Whole seconds: the server reads an integer, and 4.5 would be a
                // design it cannot even read, let alone refuse in words.
                onChange={(e) =>
                  set(i, {
                    ...item,
                    seconds: e.target.value === '' ? undefined : Math.round(Number(e.target.value)) || undefined,
                  })
                }
              />
              {say('designs.media.seconds')}
            </label>
            {item.kind === 'video' && (
              <Switch
                className="studio-item__sound"
                size="sm"
                aria-label={say('designs.media.soundFor', { name })}
                isSelected={item.sound === true}
                onChange={(on) => set(i, withSound(item, on))}
              >
                <Switch.Content>
                  {say('designs.media.sound')}
                  <Switch.Control>
                    <Switch.Thumb />
                  </Switch.Control>
                </Switch.Content>
              </Switch>
            )}
          </li>
        )
      })}
      </ol>
      )}
      <StudioSelect
        label={say('designs.media.add')}
        value=""
        onChange={(assetId) => {
          const asset = assets.find((a) => a.id === assetId)
          const kind = asset && mediaKindOf(asset.contentType)
          if (asset && kind) onAdd(asset, kind)
        }}
        options={[
          { value: '', label: say('designs.media.choose') },
          ...assets.map((asset) => {
            const kind = mediaKindOf(asset.contentType)
            return { value: asset.id, label: kind ? `${asset.filename} · ${say(KIND_KEYS[kind])}` : asset.filename }
          }),
        ]}
      />
      {/* A div, not a label: a label must not wrap a button. No caption: the
          button says "Upload a new one" itself, and saying it twice says nothing.
          The picker empties its input after every choice, so the same file can be
          chosen again. */}
      <div className="studio-field">
        <StudioFilePicker
          accept={DESIGN_MEDIA_ACCEPT}
          label={say('designs.media.upload')}
          disabled={uploading}
          onFile={onUpload}
          tip={say('designs.media.uploadHint')}
        />
      </div>
    </div>
  )
}
